import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import CustomSelect from "../../../components/ui/CustomSelect";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import { Button } from "../../../components/ui/button";
import Input from "../../../components/ui/Input";
import { useNotification } from "../../../context/NotificationContext";
import api from "../../../api";

const PRODUCT_TYPES = [
  { label: "SCPI", value: "SCPI" },
  { label: "OPCI", value: "OPCI" },
  { label: "Club-deal Immobilier", value: "CLUB_DEAL" },
  { label: "Private Equity / Capital-Investissement", value: "PRIVATE_EQUITY_FUND" },
  { label: "Assurance-Vie / Capitalisation", value: "LIFE_INSURANCE_CONTRACT" },
  { label: "Produit Structuré", value: "STRUCTURED_PRODUCT" },
  { label: "Obligations", value: "BOND" },
  { label: "Financement Participatif / Crowdfunding", value: "CROWDFUNDING" },
  { label: "OPCVM", value: "OPCVM" },
  { label: "ETF", value: "ETF" },
  { label: "SICAV", value: "SICAV" },
  { label: "FCP", value: "FCP" },
  { label: "FIA", value: "FIA" },
  { label: "Autre", value: "OTHER" },
];

export default function ProduitsForm({ open, setOpen, produitSelectionne, fetchProduits }) {
  const { showToast } = useNotification();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [formValues, setFormValues] = useState({
    nom_produit: "",
    code_isin: "",
    societe_gestion: "",
    description: "",
    site_web: "",
    objectif: "",
    indice_reference: "",
    type: "",
    risque: "",
    duree_recommandee: "",
    depositaire: "",
    commentaires: "",
    lien_dic: "",
    allocation_taux: "",
    allocation_actions: "",
    allocation_opc: "",
    allocation_convertibles: "",
    derives: "",
    sensibilite_taux: "",
  });

  const [societes, setSocietes] = useState([]);

  useEffect(() => {
    api.get(`/partenaires/noms-societes`)
      .then((res) => {
        if (Array.isArray(res.data)) {
          setSocietes(res.data);
        } else {
          setSocietes([]);
        }
      })
      .catch((err) => {
        console.error("Erreur lors du chargement des sociétés :", err);
        setSocietes([]);
      });
  }, []);

  useEffect(() => {
    if (produitSelectionne) {
      setFormValues({
        ...produitSelectionne,
        allocation_taux: produitSelectionne.strategie?.allocation?.taux || "",
        allocation_actions: produitSelectionne.strategie?.allocation?.actions || "",
        allocation_opc: produitSelectionne.strategie?.allocation?.opc || "",
        allocation_convertibles: produitSelectionne.strategie?.allocation?.convertibles || "",
        derives: produitSelectionne.strategie?.derives || "",
        sensibilite_taux: produitSelectionne.strategie?.sensibilite_taux || "",
      });
    }
  }, [produitSelectionne]);

  const handlePreSubmit = () => {
    const requiredFields = ["nom_produit", "code_isin", "societe_gestion", "type", "risque"];
    const missingFields = requiredFields.filter(field => !formValues[field]);

    if (missingFields.length > 0) {
      showToast("Veuillez remplir tous les champs obligatoires.", "error");
      return;
    }

    setIsConfirmOpen(true);
  };

  const confirmSubmit = async () => {
    setIsConfirmOpen(false);

    try {
      const produitData = {
        ...formValues,
        allocation_taux: formValues.allocation_taux || 0,
        allocation_actions: formValues.allocation_actions || 0,
        allocation_opc: formValues.allocation_opc || 0,
        allocation_convertibles: formValues.allocation_convertibles || 0,
        derives: formValues.derives || 0,
        sensibilite_taux: formValues.sensibilite_taux || 0,
      };

      const endpoint = produitSelectionne
        ? `/produits/${produitSelectionne.id}`
        : `/produits`;

      if (produitSelectionne) {
        await api.put(endpoint, produitData);
      } else {
        await api.post(endpoint, produitData);
      }

      fetchProduits();
      showToast("Produit enregistré avec succès !", "success");
      handleClose();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde du produit :", error);
      showToast("Erreur lors de la sauvegarde.", "error");
    }
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      setFormValues({
        nom_produit: "",
        code_isin: "",
        societe_gestion: "",
        description: "",
        site_web: "",
        objectif: "",
        indice_reference: "",
        type: "",
        risque: "",
        duree_recommandee: "",
        depositaire: "",
        commentaires: "",
        lien_dic: "",
        allocation_taux: "",
        allocation_actions: "",
        allocation_opc: "",
        allocation_convertibles: "",
        derives: "",
        sensibilite_taux: "",
      });
    }, 300);
  };

  const societeOptions = societes.map(s => ({ label: s, value: s }));

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />

          {/* Modal Content */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 10 }}
            transition={{ type: "spring", bounce: 0.3, duration: 0.4 }}
            className="relative w-full max-w-4xl max-h-[90vh] bg-surface rounded-xl shadow-2xl flex flex-col overflow-hidden border border-[var(--hp-color-border-muted)]"
          >
            {/* Header (sticky) */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-[var(--hp-color-border-muted)] bg-workspace/50 backdrop-blur-md">
              <h2 className="text-xl font-bold text-primary">
                {produitSelectionne ? "Modifier le produit" : "Ajouter un produit"}
              </h2>
              <Button variant="ghost" size="icon" onClick={handleClose}>
                <X className="w-5 h-5 text-subtle hover:text-primary" />
              </Button>
            </div>

            {/* Body (scrollable) */}
            <div className="p-6 overflow-y-auto custom-scrollbar">
              {/* SECTION 1 - Informations Générales */}
              <h3 className="text-sm font-bold uppercase tracking-wider text-primary mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-[var(--hp-info-100)] text-[var(--hp-info-700)] flex items-center justify-center text-xs font-bold">1</span>
                Informations Générales
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <Input
                  type="text"
                  placeholder="Nom du Produit*"
                  value={formValues.nom_produit}
                  onChange={(e) => setFormValues({ ...formValues, nom_produit: e.target.value })}
                />
                <Input
                  type="text"
                  placeholder="Code ISIN*"
                  value={formValues.code_isin}
                  onChange={(e) => setFormValues({ ...formValues, code_isin: e.target.value })}
                  className="font-mono"
                />
                <CustomSelect
                  value={formValues.societe_gestion}
                  onChange={(val) => setFormValues({ ...formValues, societe_gestion: val })}
                  options={societeOptions}
                  placeholder="Société de gestion*"
                />
                <CustomSelect
                  value={formValues.type}
                  onChange={(val) => setFormValues({ ...formValues, type: val })}
                  options={PRODUCT_TYPES}
                  placeholder="Type de produit*"
                />
                <Input
                  type="number"
                  placeholder="Risque (1 à 7)*"
                  value={formValues.risque}
                  onChange={(e) => setFormValues({ ...formValues, risque: e.target.value })}
                  min="1"
                  max="7"
                />
                <Input
                  type="url"
                  placeholder="Site Web"
                  value={formValues.site_web}
                  onChange={(e) => setFormValues({ ...formValues, site_web: e.target.value })}
                />
              </div>

              {/* SECTION 2 - Caractéristiques Financières */}
              <h3 className="text-sm font-bold uppercase tracking-wider text-primary mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-[var(--hp-success-100)] text-[var(--hp-success-700)] flex items-center justify-center text-xs font-bold">2</span>
                Caractéristiques Financières
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <Input
                  type="text"
                  placeholder="Objectif"
                  value={formValues.objectif}
                  onChange={(e) => setFormValues({ ...formValues, objectif: e.target.value })}
                />
                <Input
                  type="text"
                  placeholder="Indice de Référence"
                  value={formValues.indice_reference}
                  onChange={(e) => setFormValues({ ...formValues, indice_reference: e.target.value })}
                />
                <Input
                  type="text"
                  placeholder="Durée Recommandée"
                  value={formValues.duree_recommandee}
                  onChange={(e) => setFormValues({ ...formValues, duree_recommandee: e.target.value })}
                />
                <Input
                  type="text"
                  placeholder="Dépositaire"
                  value={formValues.depositaire}
                  onChange={(e) => setFormValues({ ...formValues, depositaire: e.target.value })}
                />
                <Input
                  type="text"
                  placeholder="Lien DIC"
                  value={formValues.lien_dic}
                  onChange={(e) => setFormValues({ ...formValues, lien_dic: e.target.value })}
                />
              </div>

              {/* SECTION 3 - Stratégie d’Investissement */}
              <h3 className="text-sm font-bold uppercase tracking-wider text-primary mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-[var(--hp-warning-100)] text-[var(--hp-warning-700)] flex items-center justify-center text-xs font-bold">3</span>
                Stratégie d'Investissement
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <Input
                  type="number"
                  placeholder="Allocation Taux (%)"
                  value={formValues.allocation_taux}
                  onChange={(e) => setFormValues({ ...formValues, allocation_taux: e.target.value })}
                />
                <Input
                  type="number"
                  placeholder="Allocation Actions (%)"
                  value={formValues.allocation_actions}
                  onChange={(e) => setFormValues({ ...formValues, allocation_actions: e.target.value })}
                />
                <Input
                  type="number"
                  placeholder="Allocation OPC (%)"
                  value={formValues.allocation_opc}
                  onChange={(e) => setFormValues({ ...formValues, allocation_opc: e.target.value })}
                />
                <Input
                  type="number"
                  placeholder="Allocation Convertibles (%)"
                  value={formValues.allocation_convertibles}
                  onChange={(e) => setFormValues({ ...formValues, allocation_convertibles: e.target.value })}
                />
                <Input
                  type="number"
                  placeholder="Dérivés (%)"
                  value={formValues.derives}
                  onChange={(e) => setFormValues({ ...formValues, derives: e.target.value })}
                />
                <Input
                  type="number"
                  placeholder="Sensibilité Taux"
                  value={formValues.sensibilite_taux}
                  onChange={(e) => setFormValues({ ...formValues, sensibilite_taux: e.target.value })}
                />
              </div>

              {/* SECTION 4 - Description et Commentaires */}
              <h3 className="text-sm font-bold uppercase tracking-wider text-primary mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-[var(--hp-primary-100)] text-[var(--hp-primary-700)] flex items-center justify-center text-xs font-bold">4</span>
                Commentaires & Description
              </h3>
              <div className="grid grid-cols-1 gap-4 mb-4">
                <textarea
                  placeholder="Description"
                  value={formValues.description}
                  onChange={(e) => setFormValues({ ...formValues, description: e.target.value })}
                  className="flex min-h-[100px] w-full rounded-[var(--hp-radius-md)] border border-[var(--hp-color-border)] bg-[var(--hp-color-surface)] px-3 py-2 text-sm text-[var(--hp-color-text)] placeholder:text-[var(--hp-color-text-subtle)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hp-color-focus)]"
                />
                <textarea
                  placeholder="Commentaires"
                  value={formValues.commentaires}
                  onChange={(e) => setFormValues({ ...formValues, commentaires: e.target.value })}
                  className="flex min-h-[100px] w-full rounded-[var(--hp-radius-md)] border border-[var(--hp-color-border)] bg-[var(--hp-color-surface)] px-3 py-2 text-sm text-[var(--hp-color-text)] placeholder:text-[var(--hp-color-text-subtle)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hp-color-focus)]"
                />
              </div>
            </div>

            {/* Footer (sticky) */}
            <div className="px-6 py-4 bg-workspace/50 backdrop-blur-md border-t border-[var(--hp-color-border-muted)] flex justify-end gap-3">
              <Button variant="secondary" onClick={handleClose}>
                Annuler
              </Button>
              <Button variant="primary" onClick={handlePreSubmit}>
                {produitSelectionne ? "Modifier" : "Ajouter le produit"}
              </Button>
            </div>
          </motion.div>

          {/* Modal de Confirmation */}
          <ConfirmModal
            isOpen={isConfirmOpen}
            title={produitSelectionne ? "Modifier le produit" : "Ajouter le produit"}
            message={produitSelectionne ? "Êtes-vous sûr de vouloir modifier les informations de ce produit ?" : "Êtes-vous sûr de vouloir ajouter ce nouveau produit au catalogue ?"}
            confirmText="Confirmer"
            cancelText="Annuler"
            type="info"
            onConfirm={confirmSubmit}
            onCancel={() => setIsConfirmOpen(false)}
          />
        </div>
      )}
    </AnimatePresence>
  );
}